import { useTranslation } from "react-i18next";
import {
  EllipsisVertical,
  ExternalLink,
  MessagesSquare,
  SquarePen,
  X,
} from "lucide-react";
import { useRef, type Ref } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils/cn";

import { MAYA_CHATS_VIEW_ID } from "../constants";
import { useChat } from "../context/use-chat";
import { useMayaSidebar } from "../context/use-maya-sidebar";
import { useMayaSpace } from "../context/use-maya-space";
import { hasRealMessages } from "../types";

import { MayaOrb } from "./maya-orb";
import { MayaShareTrigger } from "./share-trigger";

interface ChatHeaderProps {
  onClose: () => void;
  /** The chat switcher is showing instead of the conversation. */
  chatsOpen: boolean;
  onToggleChats: () => void;
  /** Focus returns here when the switcher closes. */
  chatsButtonRef: Ref<HTMLButtonElement>;
  /** Start over (the panel also moves focus to the composer). */
  onNewChat: () => void;
}

const ICON_BUTTON =
  "rounded-full text-muted-foreground hover:text-foreground hover:bg-surface/80";

/**
 * Maya's presence, what Maya is doing, and the chat's controls. The narrow
 * sidebar keeps the frequent ones as icons (share, chats, new chat, close) and
 * the rest — read aloud, temporary chat, open in full page — in a menu.
 */
export const ChatHeader = ({
  onClose,
  chatsOpen,
  onToggleChats,
  chatsButtonRef,
  onNewChat,
}: ChatHeaderProps) => {
  const { t } = useTranslation(["common"]);
  const {
    messages,
    sessionId,
    readAloud,
    toggleReadAloud,
    isTyping,
    status,
    temporary,
    toggleTemporary,
  } = useChat();
  const { headerOrbRef, warp } = useMayaSidebar();
  const space = useMayaSpace();
  // The warp's far end: absent while the stars are on their way in, gone the
  // moment they leave on the way out; pops in with an overshoot as they land.
  const orbHidden = warp?.phase === "travelling";
  const canReset = hasRealMessages(messages);
  // Continue the same conversation in its space's full page. The sidebar
  // closes by itself there, its stars flying into the page (see
  // MayaSidebarProvider).
  const navigate = useNavigate();
  const fullPagePath = space.fullPage(sessionId);
  // Leaving for the full page: focus goes to its composer, not back to this
  // menu's trigger as the menu closes.
  const leavingRef = useRef(false);

  return (
    // A container: the badge steps aside when the sidebar is dragged narrow,
    // so the controls never crush Maya's name.
    <div className="@container flex items-center gap-3 px-4 pt-4 pb-3">
      <span
        ref={headerOrbRef}
        className={cn(
          "shrink-0 transition-[opacity,scale]",
          orbHidden
            ? "scale-25 opacity-0 duration-150 ease-in"
            : "scale-100 opacity-100 duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        )}
      >
        <MayaOrb size={40} thinking={isTyping} />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        {/* A <div>, not a <p>: Badge renders a <div>, which a <p> can't hold. */}
        <div className="flex items-center gap-1.5 text-sm font-bold">
          <span className="maya-gradient-text">
            {t("common:maya.assistant_name")}
          </span>
          {temporary ? (
            // Always shown (not hidden when narrow): it changes what happens
            // to the conversation.
            <Badge variant="warning" className="px-1.5 py-0 text-[10px]">
              {t("common:ai_assistant.temporary")}
            </Badge>
          ) : (
            // Which space's chats these are — the list changes with it.
            <Badge
              variant="primary"
              className="px-1.5 py-0 text-[10px] @max-[20rem]:hidden"
            >
              {t(space.labelKey)}
            </Badge>
          )}
        </div>
        <p
          // Not a live region: the typing row in the message log already
          // announces that Maya is writing, once.
          className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground"
        >
          {isTyping ? (
            <span className="maya-shimmer-text truncate">
              {status ?? t("common:maya.thinking")}
            </span>
          ) : (
            <>
              <span className="size-1.5 shrink-0 rounded-full bg-success" />
              <span className="truncate">
                {temporary
                  ? t("common:ai_assistant.temporary_chat_nothing_stored")
                  : t("common:maya.online_ready_to_help")}
              </span>
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center">
        {/* Share the open chat — saved chats only, as on the full page. */}
        {sessionId && !temporary && (
          <MayaShareTrigger
            sessionId={sessionId}
            triggerClassName={cn(ICON_BUTTON, "size-8 border-0 bg-transparent")}
          />
        )}
        <Button
          ref={chatsButtonRef}
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onToggleChats}
          aria-expanded={chatsOpen}
          aria-controls={chatsOpen ? MAYA_CHATS_VIEW_ID : undefined}
          className={cn(
            ICON_BUTTON,
            chatsOpen && "bg-primary-muted text-primary",
          )}
          aria-label={t("common:maya.show_chats")}
          title={t("common:maya.show_chats")}
        >
          <MessagesSquare className="size-4" />
        </Button>
        {canReset && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onNewChat}
            // Starting over mid-reply would cut that reply off.
            disabled={isTyping}
            className={ICON_BUTTON}
            aria-label={t("common:a11y.start_new_chat")}
            title={t("common:a11y.new_chat")}
          >
            <SquarePen className="size-4" />
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={ICON_BUTTON}
              aria-label={t("common:a11y.more_actions")}
              title={t("common:a11y.more_actions")}
            >
              <EllipsisVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-60"
            onCloseAutoFocus={(e) => {
              if (!leavingRef.current) return;
              leavingRef.current = false;
              e.preventDefault();
            }}
          >
            <DropdownMenuCheckboxItem
              checked={readAloud}
              onCheckedChange={toggleReadAloud}
            >
              {t("common:chrome.read_responses_aloud")}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={temporary}
              onCheckedChange={toggleTemporary}
              // Switching starts a fresh chat — not while a reply is coming.
              disabled={isTyping}
            >
              {/* "Temporary chat — not saved to your history …": the title and
                  what it means, in one line. */}
              {t("common:ai_assistant.temporary_chat_not_saved")}
            </DropdownMenuCheckboxItem>
            {/* A temporary chat isn't saved, so the full page can't open it. */}
            {!temporary && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  // The answer isn't saved until it has arrived: the full
                  // page would open the chat without it.
                  disabled={isTyping}
                  onSelect={() => {
                    leavingRef.current = true;
                    navigate(fullPagePath);
                  }}
                >
                  <ExternalLink className="size-4" />
                  {t("common:a11y.open_in_full_page")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          className={ICON_BUTTON}
          aria-label={t("common:a11y.close_maya_copilot")}
          title={t("common:a11y.close_maya_copilot")}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
};
